import { createLogger, Logger } from '../utils/Logger';

export const PROFILE_SCHEMA_VERSION = 1;

export type RunResult = 'victory' | 'defeat';
export type SupportedLanguage = 'en' | 'ru';

export interface RunSummary {
  readonly result: RunResult;
  readonly survivedSeconds: number;
  readonly level: number;
  readonly bossesKilled: number;
  readonly endedAt: string;
}

export interface ProfileRecords {
  readonly bestSurvivedSeconds: number;
  readonly highestLevel: number;
  readonly bossesKilled: number;
  readonly totalRuns: number;
  readonly victories: number;
  readonly defeats: number;
  readonly lastRun: RunSummary | null;
}

export interface ProfileUnlocks {
  readonly weaponIds: readonly string[];
}

export interface ProfileSettings {
  readonly musicVolume: number;
  readonly sfxVolume: number;
  readonly mute: boolean;
  readonly language: SupportedLanguage | null;
  readonly gameplay: Record<string, boolean | number | string>;
}

export interface PlayerProfile {
  readonly schemaVersion: number;
  readonly records: ProfileRecords;
  readonly unlocks: ProfileUnlocks;
  readonly settings: ProfileSettings;
  readonly meta: Record<string, unknown>;
}

export interface NormalizeProfileOptions {
  readonly defaultWeaponIds?: readonly string[];
  readonly logger?: Logger;
}

export interface ProfileRunCommitResult {
  readonly profile: PlayerProfile;
  readonly changed: boolean;
  readonly deltas: {
    readonly bestSurvivedSeconds: boolean;
    readonly highestLevel: boolean;
    readonly bossesKilled: boolean;
  };
}

const DEFAULT_RECORDS: ProfileRecords = Object.freeze({
  bestSurvivedSeconds: 0,
  highestLevel: 1,
  bossesKilled: 0,
  totalRuns: 0,
  victories: 0,
  defeats: 0,
  lastRun: null
});

const DEFAULT_SETTINGS: ProfileSettings = Object.freeze({
  musicVolume: 0.3,
  sfxVolume: 0.7,
  mute: false,
  language: null,
  gameplay: Object.freeze({ reduceShake: false, reduceFlashes: false })
});

export function createDefaultProfile(defaultWeaponIds: readonly string[] = [], logger: Logger = createLogger('ProfileState')): PlayerProfile {
  const weaponIds = normalizeWeaponIds(defaultWeaponIds, logger);
  logger.debug('createDefaultProfile:complete', { schemaVersion: PROFILE_SCHEMA_VERSION, weaponIds });
  return deepFreezeProfile({
    schemaVersion: PROFILE_SCHEMA_VERSION,
    records: { ...DEFAULT_RECORDS },
    unlocks: { weaponIds },
    settings: { ...DEFAULT_SETTINGS, gameplay: { ...DEFAULT_SETTINGS.gameplay } },
    meta: {}
  });
}

export function normalizeProfile(value: unknown, options: NormalizeProfileOptions = {}): PlayerProfile {
  const logger = options.logger ?? createLogger('ProfileState');
  const defaults = createDefaultProfile(options.defaultWeaponIds ?? [], logger);

  if (!isObject(value)) {
    logger.warn('normalizeProfile:invalid-root-defaults-used', { receivedType: typeof value });
    return defaults;
  }

  const schemaVersion = normalizeSchemaVersion(value.schemaVersion, logger);
  if (schemaVersion > PROFILE_SCHEMA_VERSION) {
    logger.warn('normalizeProfile:future-schema-version-defaults-used', { schemaVersion, supported: PROFILE_SCHEMA_VERSION });
    return defaults;
  }

  return deepFreezeProfile({
    schemaVersion: PROFILE_SCHEMA_VERSION,
    records: normalizeRecords(value.records, defaults.records, logger),
    unlocks: normalizeUnlocks(value.unlocks, defaults.unlocks, logger),
    settings: normalizeSettings(value.settings, defaults.settings, logger),
    meta: isObject(value.meta) ? { ...value.meta } : {}
  });
}

export function commitRunSummary(profile: PlayerProfile, summary: RunSummary, logger: Logger = createLogger('ProfileState')): ProfileRunCommitResult {
  const safeSummary = normalizeRunSummary(summary, logger);
  const records = profile.records;
  const deltas = {
    bestSurvivedSeconds: safeSummary.survivedSeconds > records.bestSurvivedSeconds,
    highestLevel: safeSummary.level > records.highestLevel,
    bossesKilled: safeSummary.bossesKilled > records.bossesKilled
  };

  const nextProfile = deepFreezeProfile({
    ...profile,
    records: {
      bestSurvivedSeconds: Math.max(records.bestSurvivedSeconds, safeSummary.survivedSeconds),
      highestLevel: Math.max(records.highestLevel, safeSummary.level),
      bossesKilled: Math.max(records.bossesKilled, safeSummary.bossesKilled),
      totalRuns: records.totalRuns + 1,
      victories: records.victories + (safeSummary.result === 'victory' ? 1 : 0),
      defeats: records.defeats + (safeSummary.result === 'defeat' ? 1 : 0),
      lastRun: safeSummary
    }
  });

  logger.debug('commitRunSummary:complete', { summary: safeSummary, deltas });
  return { profile: nextProfile, changed: true, deltas };
}

export function updateProfileSettings(profile: PlayerProfile, patch: Partial<ProfileSettings>, logger: Logger = createLogger('ProfileState')): PlayerProfile {
  const nextSettings = normalizeSettings({ ...profile.settings, ...patch, gameplay: { ...profile.settings.gameplay, ...patch.gameplay } }, profile.settings, logger);
  logger.debug('updateProfileSettings:complete', { patchKeys: Object.keys(patch) });
  return deepFreezeProfile({ ...profile, settings: nextSettings });
}

export function addUnlockedWeapon(profile: PlayerProfile, weaponId: string, logger: Logger = createLogger('ProfileState')): { profile: PlayerProfile; changed: boolean } {
  const normalized = weaponId.trim();
  if (!normalized) {
    logger.warn('addUnlockedWeapon:empty-id-ignored');
    return { profile, changed: false };
  }
  if (profile.unlocks.weaponIds.includes(normalized)) {
    logger.debug('addUnlockedWeapon:already-unlocked', { weaponId: normalized });
    return { profile, changed: false };
  }
  const nextIds = normalizeWeaponIds([...profile.unlocks.weaponIds, normalized], logger);
  logger.info('addUnlockedWeapon:persisted', { weaponId: normalized, count: nextIds.length });
  return {
    profile: deepFreezeProfile({ ...profile, unlocks: { weaponIds: nextIds } }),
    changed: true
  };
}

function normalizeRecords(value: unknown, defaults: ProfileRecords, logger: Logger): ProfileRecords {
  if (!isObject(value)) {
    logger.debug('normalizeRecords:defaults-used');
    return defaults;
  }
  const totalRuns = clampInteger(value.totalRuns, defaults.totalRuns, 'records.totalRuns', logger);
  const victories = clampInteger(value.victories, defaults.victories, 'records.victories', logger);
  const defeats = clampInteger(value.defeats, defaults.defeats, 'records.defeats', logger);
  const normalizedCounters = normalizeRunCounters(totalRuns, victories, defeats, logger);

  return {
    bestSurvivedSeconds: clampNonNegative(value.bestSurvivedSeconds, defaults.bestSurvivedSeconds, 'records.bestSurvivedSeconds', logger),
    highestLevel: Math.max(1, clampInteger(value.highestLevel, defaults.highestLevel, 'records.highestLevel', logger)),
    bossesKilled: clampNonNegative(value.bossesKilled, defaults.bossesKilled, 'records.bossesKilled', logger),
    totalRuns: normalizedCounters.totalRuns,
    victories: normalizedCounters.victories,
    defeats: normalizedCounters.defeats,
    lastRun: value.lastRun === null || value.lastRun === undefined ? null : normalizeRunSummary(value.lastRun, logger)
  };
}

function normalizeRunCounters(totalRuns: number, victories: number, defeats: number, logger: Logger): Pick<ProfileRecords, 'totalRuns' | 'victories' | 'defeats'> {
  const normalizedVictories = Math.min(victories, totalRuns);
  const normalizedDefeats = Math.min(defeats, Math.max(0, totalRuns - normalizedVictories));
  if (normalizedVictories !== victories || normalizedDefeats !== defeats) {
    logger.warn('normalizeRunCounters:cross-field-invariant-normalized', {
      totalRuns,
      victories,
      defeats,
      normalizedVictories,
      normalizedDefeats
    });
  }
  return {
    totalRuns,
    victories: normalizedVictories,
    defeats: normalizedDefeats
  };
}

function normalizeUnlocks(value: unknown, defaults: ProfileUnlocks, logger: Logger): ProfileUnlocks {
  if (!isObject(value)) {
    logger.debug('normalizeUnlocks:defaults-used', { defaultWeaponIds: defaults.weaponIds });
    return defaults;
  }
  return { weaponIds: normalizeWeaponIds(Array.isArray(value.weaponIds) ? value.weaponIds : defaults.weaponIds, logger) };
}

function normalizeSettings(value: unknown, defaults: ProfileSettings, logger: Logger): ProfileSettings {
  if (!isObject(value)) {
    logger.debug('normalizeSettings:defaults-used');
    return defaults;
  }
  return {
    musicVolume: clampVolume(value.musicVolume, defaults.musicVolume, 'settings.musicVolume', logger),
    sfxVolume: clampVolume(value.sfxVolume, defaults.sfxVolume, 'settings.sfxVolume', logger),
    mute: typeof value.mute === 'boolean' ? value.mute : defaults.mute,
    language: normalizeLanguage(value.language, logger),
    gameplay: normalizeGameplay(value.gameplay, logger)
  };
}

function normalizeRunSummary(value: unknown, logger: Logger): RunSummary {
  const source = isObject(value) ? value : {};
  const result = source.result === 'victory' ? 'victory' : 'defeat';
  return {
    result,
    survivedSeconds: clampNonNegative(source.survivedSeconds, 0, 'lastRun.survivedSeconds', logger),
    level: Math.max(1, clampInteger(source.level, 1, 'lastRun.level', logger)),
    bossesKilled: clampInteger(source.bossesKilled, 0, 'lastRun.bossesKilled', logger),
    endedAt: typeof source.endedAt === 'string' && source.endedAt.length > 0 ? source.endedAt : new Date(0).toISOString()
  };
}

function normalizeWeaponIds(value: readonly unknown[], logger: Logger): readonly string[] {
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item !== 'string' || item.trim().length === 0) {
      logger.warn('normalizeWeaponIds:invalid-id-ignored', { item });
      continue;
    }
    seen.add(item.trim());
  }
  return [...seen].sort();
}

function normalizeSchemaVersion(value: unknown, logger: Logger): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
    logger.debug('normalizeSchemaVersion:defaulted', { value, schemaVersion: PROFILE_SCHEMA_VERSION });
    return PROFILE_SCHEMA_VERSION;
  }
  return value;
}

function normalizeLanguage(value: unknown, logger: Logger): SupportedLanguage | null {
  if (value === null || value === undefined) return null;
  if (value === 'en' || value === 'ru') return value;
  logger.warn('normalizeLanguage:unsupported-defaulted', { value });
  return null;
}

function normalizeGameplay(value: unknown, logger: Logger): Record<string, boolean | number | string> {
  const source = isObject(value) ? value : {};
  const result = Object.fromEntries(Object.entries(source).filter(([, entry]) => {
    return typeof entry === 'boolean' || typeof entry === 'number' || typeof entry === 'string';
  })) as Record<string, boolean | number | string>;
  for (const key of ['reduceShake', 'reduceFlashes']) {
    if (source[key] !== undefined && typeof source[key] !== 'boolean') logger.warn('normalizeGameplay:invalid-boolean', { key, value: source[key] });
    result[key] = source[key] === true;
  }
  logger.debug('normalizeGameplay:effects', { reduceShake: result.reduceShake, reduceFlashes: result.reduceFlashes });
  return result;
}

function clampVolume(value: unknown, fallback: number, path: string, logger: Logger): number {
  const numberValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numberValue)) {
    logger.warn('clampVolume:invalid-defaulted', { path, value, fallback });
    return fallback;
  }
  const clamped = Math.min(1, Math.max(0, numberValue));
  if (clamped !== numberValue) {
    logger.warn('clampVolume:out-of-range-normalized', { path, value, clamped });
  }
  return clamped;
}

function clampInteger(value: unknown, fallback: number, path: string, logger: Logger): number {
  const numberValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numberValue)) {
    logger.warn('clampInteger:invalid-defaulted', { path, value, fallback });
    return fallback;
  }
  return Math.max(0, Math.floor(numberValue));
}

function clampNonNegative(value: unknown, fallback: number, path: string, logger: Logger): number {
  const numberValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numberValue)) {
    logger.warn('clampNonNegative:invalid-defaulted', { path, value, fallback });
    return fallback;
  }
  return Math.max(0, numberValue);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deepFreezeProfile(profile: PlayerProfile): PlayerProfile {
  return deepFreeze(profile);
}

function deepFreeze<T>(value: T): T {
  if ((typeof value !== 'object' && typeof value !== 'function') || value === null || Object.isFrozen(value)) return value;
  Object.values(value as Record<string, unknown>).forEach(deepFreeze);
  return Object.freeze(value);
}
